import { useTranslation } from 'react-i18next';
import {
  Outlet,
  useNavigate,
  useLocation,
  createFileRoute,
} from '@tanstack/react-router';

import { Box, Tab, Tabs, Button, Container, Typography } from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Architecture as ArchitectureIcon,
} from '@mui/icons-material';

import { useState } from 'react';
import { paths } from '@/routes/paths';
import { useUserPermissions } from '@/hooks/use-user-permissions';
import { FEATURE_FLAGS, useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useBillingControllerGetQuota } from '@/apis/backend/generated/billing/billing';
import { UpgradeModal } from '@/components/billing/UpgradeModal';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/templates')({
  component: TemplatesLayout,
});

function activeTabFromPath(pathname: string): 'library' | 'my' {
  if (pathname.endsWith('/templates/my')) return 'my';
  return 'library';
}

function TemplatesLayout() {
  const { t } = useTranslation('templates');
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, clientId } = Route.useParams();
  const tp = paths.dashboard.tenant(orgId, clientId);
  const base = paths.dashboard.tenantRoot(orgId, clientId);

  // The email builder is a canvas surface, so its entry point follows the same
  // gate as the rest of the canvas features: platform admins always, plus orgs
  // opted into the canvas_creative_modes flag.
  const { isPlatformAdmin } = useUserPermissions();
  const canvasModesEnabled = useFeatureFlag(FEATURE_FLAGS.CANVAS_CREATIVE_MODES);
  // Both header buttons and the Library tab are DIY doorways: they mint a new
  // email, which a brand without the self-serve builder cannot do. "My" stays,
  // so a managed brand still has its own emails and its Figma imports here.
  const selfServeEnabled = useFeatureFlag(FEATURE_FLAGS.SELF_SERVE_BUILDER);
  const showBuilderShortcut = selfServeEnabled && (isPlatformAdmin || canvasModesEnabled);

  // Both header buttons land on a doorway that mints a new template, so an
  // over-quota user gets the upgrade prompt rather than a page that can only
  // 403. Undefined while loading means "allow" — the server is the real gate.
  const { data: quota } = useBillingControllerGetQuota(orgId, undefined, {
    query: { enabled: !!orgId },
  });
  const quotaExceeded = !!quota && !quota.canGenerate;
  const [showUpgrade, setShowUpgrade] = useState(false);

  const goOrUpgrade = (to: string) => {
    if (quotaExceeded) {
      setShowUpgrade(true);
      return;
    }
    navigate({ to });
  };

  // Tabs warns when `value` names a tab it is not rendering, and the library
  // route forwards to `my` for these brands, so the selection follows the tab
  // that survives rather than the URL it is about to leave.
  const activeTab = selfServeEnabled ? activeTabFromPath(location.pathname) : 'my';

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'library' | 'my') => {
    if (newValue === 'library') navigate({ to: `${base}/templates/library` });
    else navigate({ to: `${base}/templates/my` });
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
      <Box
        sx={{
          mb: { xs: 2, sm: 4 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          sx={{
            fontSize: { xs: 24, sm: 'inherit' },
            lineHeight: 1.2,
            flex: 1,
            minWidth: 0,
          }}
        >
          {t('page.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
          {/* "Open Email Builder" is a power-user shortcut; hidden on mobile
              to keep the header from wrapping. Gated behind the canvas flag
              (platform admins or canvas_creative_modes orgs). */}
          {showBuilderShortcut && (
            <Button
              variant="outlined"
              startIcon={<ArchitectureIcon />}
              onClick={() => goOrUpgrade(tp.emailNew)}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              {t('page.openBuilder')}
            </Button>
          )}
          {selfServeEnabled && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => goOrUpgrade(tp.emailCreate)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('page.createWithAI')}
            </Button>
          )}
        </Box>
      </Box>

      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        type="generation"
        organizationId={orgId}
        used={quota?.used ?? 0}
        limit={quota?.limit ?? 0}
        nextPlanName={quota?.nextPlanName}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          {selfServeEnabled && <Tab label={t('tabs.library')} value="library" />}
          <Tab label={t('tabs.my')} value="my" />
        </Tabs>
      </Box>

      <Outlet />
    </Container>
  );
}
